<template xmlns:v-slot="http://www.w3.org/1999/XSL/Transform">
   <b-container>
      <!-- Header and Create New Batch Button -->
      <b-row class="mb-4">
         <b-col class="text-right">
            <b-button variant="primary" @click="showCreateModal">
               Create New Batch
            </b-button>
         </b-col>
      </b-row>

      <!-- Loading Overlay -->
      <b-overlay :show="isLoading" rounded="sm">
         <b-table
             :items="batches"
             :fields="fields"
             :current-page="currentPage"
             :per-page="perPage"
             :total-rows="totalRows"
             striped
             hover
             responsive
         >
            <!-- Update status cell template -->
            <template v-slot:cell(status)="data">
               <div>
                  <b-badge :variant="getStatusVariant(data.item)" class="mr-2">
                     {{ data.item.statusImageAnalysis }}
                  </b-badge>
                  <br>
                  <b-badge :variant="getEmbeddingsStatusVariant(data.item)">
                     {{ data.item.statusEmbeddings }}
                  </b-badge>
               </div>
            </template>

            <!-- Add batch IDs cell template -->
            <template v-slot:cell(batchIds)="data">
               <small class="d-block text-muted">
                  <div>Analysis: {{ data.item.imageAnalysisBatchId }}</div>
                  <div>Embeddings: {{ data.item.embeddingsBatchId || '-' }}</div>
               </small>
            </template>

            <!-- Update dates cell template -->
            <template v-slot:cell(dates)="data">
               <div>
                  <small class="d-block">Created: {{ formatDate(data.item.createdAt) }}</small>
                  <small class="d-block">Updated: {{ formatDate(data.item.updatedAt) }}</small>
               </div>
            </template>
            <template v-slot:cell(actions)="data">
               <b-button-group size="sm">
                  <b-button
                      variant="outline-secondary"
                      @click="refreshBatch(data.item)"
                      :disabled="!canRefresh(data.item)"
                      v-b-tooltip.hover
                      title="Refresh analysis status"
                  >
                     <i class="fal fa-sync"></i>
                  </b-button>
                  <b-button
                      variant="info"
                      @click="showDetails(data.item)"
                  >
                     Details
                  </b-button>
               </b-button-group>
            </template>
            <!-- Other templates remain the same -->
         </b-table>

         <!-- Pagination remains the same -->
      </b-overlay>

      <!-- Create Batch Modal -->
      <b-modal
          id="create-batch-modal"
          title="Create New Batch Analysis"
          @ok="createBatch"
          @hidden="resetModal"
      >
         <form ref="form" @submit.stop.prevent="createBatch">
            <b-form-group
                label="Batch Label"
                label-for="batch-label"
                :state="batchLabelState"
                :invalid-feedback="batchLabelFeedback"
            >
               <b-form-input
                   id="batch-label"
                   v-model="newBatch.batchLabel"
                   :state="batchLabelState"
                   required
                   trim
               ></b-form-input>
            </b-form-group>

            <b-form-group
                label="Analysis Prompt (Optional)"
                label-for="batch-prompt"
            >
               <b-form-textarea
                   id="batch-prompt"
                   v-model="newBatch.prompt"
                   rows="3"
               ></b-form-textarea>
            </b-form-group>
         </form>
      </b-modal>

      <!-- Details Modal -->
      <b-modal
          id="details-modal"
          title="Batch Details"
          size="lg"
          hide-footer
      >
         <template v-if="selectedBatch">
            <dl class="row">
               <dt class="col-sm-4">Batch Label</dt>
               <dd class="col-sm-8">{{ selectedBatch.batchLabel }}</dd>

               <dt class="col-sm-4">Created At</dt>
               <dd class="col-sm-8">{{ formatDate(selectedBatch.createdAt) }}</dd>

               <dt class="col-sm-4">Image Analysis Status</dt>
               <dd class="col-sm-8">
                  <b-badge :variant="getStatusVariant(selectedBatch)">
                     {{ selectedBatch.statusImageAnalysis }}
                  </b-badge>
               </dd>

               <dt class="col-sm-4">Embeddings Status</dt>
               <dd class="col-sm-8">
                  <b-badge :variant="getEmbeddingsStatusVariant(selectedBatch)">
                     {{ selectedBatch.statusEmbeddings }}
                  </b-badge>
               </dd>

               <template v-if="selectedBatch.errorDetails">
                  <dt class="col-sm-4">Error Details</dt>
                  <dd class="col-sm-8 text-danger">{{ selectedBatch.errorDetails }}</dd>
               </template>


            </dl>
         </template>
      </b-modal>
   </b-container>
</template>

<script>
import ArticleApi from "@shared/_api/article";

export default {
   name: 'ai',
   data() {
      return {
         batches: [],
         isLoading: false,
         // Add pagination related data
         currentPage: 1,
         perPage: 20,
         totalRows: 0,
         fields: [
            {
               key: 'batchLabel',
               label: 'Label',
               sortable: true
            },
            {
               key: 'prompt',
               label: 'Prompt',
               sortable: true
            },
            {
               key: 'batchIds',
               label: 'Batch IDs'
            },
            {
               key: 'status',
               label: 'Status'
            },
            {
               key: 'imageCount',
               label: 'Anzahl Bilder'
            },
            {
               key: 'dates',
               label: 'Dates'
            },
            {
               key: 'actions',
               label: 'Actions'
            }
         ],
         newBatch: {
            batchLabel: '',
            prompt: 'Describe the image which is some kind of clothing accessoires. Focus mainly on form, color and tone.'
         },
         selectedBatch: null,
         refreshInterval: null
      }
   },
   computed: {
      batchLabelState() {
         return this.newBatch.batchLabel.length > 0;
      },
      batchLabelFeedback() {
         if (this.newBatch.batchLabel.length === 0) {
            return 'Batch label is required';
         }
         return '';
      }
   },
   created() {
      this.load();
   },
   methods: {
      canRefresh(batch) {
         // Allow refresh for batches that are not in CREATING or PROCESSING state
         return !['CREATING', 'PROCESSING'].includes(batch.statusImageAnalysis);
      },

      async refreshBatch(batch) {
         try {
            this.isLoading = true;
            await ArticleApi.refreshAnalysis(batch.id);

            // Show success message
            this.$bvToast.toast('Analysis refresh initiated successfully', {
               title: 'Success',
               variant: 'success',
               solid: true
            });

            // Reload the data
            await this.load();
         } catch (error) {
            console.error('Failed to refresh analysis:', error);

         } finally {
            this.isLoading = false;
         }
      },
      formatDate(dateArray) {
         return '-';
         /*if (!dateArray || !Array.isArray(dateArray)) return '-';

         const [year, month, day, hour, minute, second] = dateArray;
         const date = new Date(year, month - 1, day, hour, minute, second);

         return new Intl.DateTimeFormat('default', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
         }).format(date);*/
      },

      getStatusVariant(batch) {
         switch (batch.statusImageAnalysis) {
            case 'COMPLETED':
               return 'success';
            case 'PROCESSING':
               return 'primary';
            case 'CREATING':
               return 'info';
            case 'FAILED':
               return 'danger';
            default:
               return 'secondary';
         }
      },

      getEmbeddingsStatusVariant(batch) {
         switch (batch.statusEmbeddings) {
            case 'COMPLETED':
               return 'success';
            case 'PROCESSING':
               return 'primary';
            case 'PENDING':
               return 'warning';
            case 'FAILED':
               return 'danger';
            default:
               return 'secondary';
         }
      },

      canStartEmbeddings(batch) {
         return batch.statusImageAnalysis === 'COMPLETED' &&
             batch.statusEmbeddings === 'PENDING';
      },
      async load() {
         try {
            this.isLoading = true;
            // Update to handle paginated response
            const response = await ArticleApi.getBatches(this.currentPage - 1, this.perPage);
            this.batches = response.content; // Spring Boot returns content array
            this.totalRows = response.totalElements; // Total number of items
            // Additional pagination info available:
            // response.totalPages - total number of pages
            // response.number - current page (0-based)
            // response.size - items per page
         } catch (error) {
            console.error('Failed to load batches:', error);
            this.$bvToast.toast('Failed to load batches', {
               title: 'Error',
               variant: 'danger'
            });
         } finally {
            this.isLoading = false;
         }
      },

      // Add pagination handler
      handlePageChange(page) {
         this.currentPage = page;
         this.load();
      },

      // When creating a new batch, reset to first page
      async createBatch(bvModalEvent) {
         bvModalEvent.preventDefault();
         if (!this.newBatch.batchLabel) {
            this.$bvToast.toast('Please enter a batch label', {
               title: 'Validation Error',
               variant: 'warning'
            });
            return;
         }

         try {
            await ArticleApi.startAnalysis(this.newBatch);
            this.$bvModal.hide('create-batch-modal');
            this.currentPage = 1; // Reset to first page
            this.load();
            this.$bvToast.toast('Batch analysis started successfully', {
               title: 'Success',
               variant: 'success'
            });
         } catch (error) {
            this.$bvToast.toast('Failed to start batch analysis', {
               title: 'Error',
               variant: 'danger'
            });
         }
      },

      // Modified refresh interval to maintain current page
      created() {
         this.load();
      },


      getStatusText(batch) {
         return `${batch.statusImageAnalysis} / ${batch.statusEmbeddings}`;
      },

      showCreateModal() {
         this.$bvModal.show('create-batch-modal');
      },


      async startEmbeddings(batchId) {
         try {
            await ArticleApi.startEmbeddings(batchId);
            this.load();
            this.$bvToast.toast('Embeddings process started successfully', {
               title: 'Success',
               variant: 'success'
            });
         } catch (error) {
            this.$bvToast.toast('Failed to start embeddings process', {
               title: 'Error',
               variant: 'danger'
            });
         }
      },

      showDetails(batch) {
         this.selectedBatch = batch;
         this.$bvModal.show('details-modal');
      },

      resetModal() {
         this.newBatch = {
            batchLabel: '',
            prompt: 'Describe the image which is some kind of clothing accessoires. Focus mainly on form, color and tone.'
         };
      }
   }
}
</script>

<style scoped>
.batch-actions {
   white-space: nowrap;
}
</style>